import { Select as RadixSelect } from "radix-ui";
import { ReactNode } from "react";
import CheckIcon from "../icons/CheckIcon";
import ChevronDownIcon from "../icons/ChevronDownIcon";

export const Select = (props: {
  children: ReactNode;
  value?: string;
  defaultValue?: string;
  onValueChange?: (value: string) => void;
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?(open: boolean): void;
  disabled?: boolean;
}) => {
  const {
    defaultValue,
    children,
    value,
    onValueChange,
    open,
    defaultOpen,
    onOpenChange,
    disabled,
  } = props;
  return (
    <RadixSelect.Root
      open={open}
      defaultOpen={defaultOpen}
      onOpenChange={onOpenChange}
      value={value}
      defaultValue={defaultValue}
      onValueChange={onValueChange}
      disabled={disabled}
    >
      {children}
    </RadixSelect.Root>
  );
};

export const SelectTrigger = ({
  ariaLabel,
  button,
  className,
  icon,
  placeholder,
  badge,
}: {
  className?: string;
  ariaLabel?: string;
  button?: boolean;
  placeholder?: string;
  badge?: boolean;
  icon?: ReactNode;
}) => {
  if (button) {
    return (
      <RadixSelect.Trigger asChild>
        <button
          type="button"
          className={`btn ${className ?? ""} ${badge ? "btn--sm" : ""}`}
          aria-label={ariaLabel}
        >
          {icon ? icon : ""}
          <RadixSelect.Value placeholder={placeholder} />
          <RadixSelect.Icon className="c-select-down-icon d-inline-flex">
            <ChevronDownIcon />
          </RadixSelect.Icon>
        </button>
      </RadixSelect.Trigger>
    );
  }
  return (
    <RadixSelect.Trigger
      className={`form-select c-select__trigger ${className ?? ""} ${
        badge ? "c-select__trigger--badge" : ""
      }`}
      aria-label={ariaLabel}
    >
      {icon ? icon : ""}
      <RadixSelect.Value placeholder={placeholder} />
      <RadixSelect.Icon className="c-select-down-icon d-inline-flex">
        <ChevronDownIcon />
      </RadixSelect.Icon>
    </RadixSelect.Trigger>
  );
};

export const SelectContent = (props: {
  children: ReactNode;
  className?: string;
  scrollable?: boolean;
  small?: boolean;
}) => {
  const { children, className, scrollable, small } = props;
  if (scrollable) {
    return (
      <RadixSelect.Portal>
        <RadixSelect.Content
          position="popper"
          collisionPadding={{ top: 100, left: 15, bottom: 15, right: 15 }}
          className={`c-select__content c-select__content--constrained ${
            className ?? ""
          }`}
        >
          <RadixSelect.ScrollUpButton className="c-select__scrollbutton c-select__scrollbutton--up">
            <ChevronDownIcon />
          </RadixSelect.ScrollUpButton>
          <RadixSelect.Viewport className="c-select__content-viewport">
            {children}
          </RadixSelect.Viewport>
          <RadixSelect.ScrollDownButton className="c-select__scrollbutton">
            <ChevronDownIcon />
          </RadixSelect.ScrollDownButton>
        </RadixSelect.Content>
      </RadixSelect.Portal>
    );
  } else {
    return (
      <RadixSelect.Portal>
        <RadixSelect.Content
          className={`c-select__content ${className ?? ""} ${
            small ? "c-select__content--sm" : ""
          }`}
        >
          <RadixSelect.Viewport className="c-select__content-viewport">
            {children}
          </RadixSelect.Viewport>
        </RadixSelect.Content>
      </RadixSelect.Portal>
    );
  }
};

interface ISelectItemProps extends RadixSelect.SelectItemProps {
  children: ReactNode;
  className?: string;
}
export const SelectItem = (props: ISelectItemProps) => {
  const { children, className, ...rest } = props;
  return (
    <RadixSelect.Item {...rest} className={`c-select__item ${className ?? ""}`}>
      <RadixSelect.ItemIndicator className="c-select__item-icon">
        <CheckIcon />
      </RadixSelect.ItemIndicator>
      <RadixSelect.ItemText>{children}</RadixSelect.ItemText>
    </RadixSelect.Item>
  );
};
